import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import AppContainer from "@src/common/app_container";
import Alert, { AlertProps } from "@src/common/alert";
import { useAuth0 } from "@auth0/auth0-react";
import { useSubmagicOutputsAPI } from "@src/api/submagic_outputs_api";
import { useGenerateMediaAPI } from "@src/api/generate_media_api";
import { useFileAPI } from "@src/api/file_api";

type PendingState = "idle" | "submitting" | "polling" | "done" | "error";

interface SubmagicCreateProjectRequest {
    file_ids: string[];
    title: string;
    language: string;
    video_url?: string;
    videoUrl?: string;
}

interface SubmagicTaskQueueResponse {
    id: string;
    status: "queued" | "completed" | "processing" | "failed";
    parentId?: string;
    name?: string;
    result?: string;
    payload?: string;
}

interface GeneratedFile {
    generativeOutputId: string;
    assetType: string;
    fileId: string;
    createdAt: string;
    permalink: string;
    thumbnailImage?: string | null;
    name?: string | null;
}

interface LanguageOption {
    code: string;
    label: string;
}

interface TemplateOption {
    id: string;
    name: string;
    preview?: string | null;
}

const FALLBACK_LANGUAGES: LanguageOption[] = [
    { code: "en", label: "English" },
    { code: "es", label: "Spanish" },
    { code: "fr", label: "French" },
];

const POLL_TIMEOUT_MS = 240_000;
const POLL_INTERVAL_MS = 2000;

function inferFilenameFromUrl(url: string | undefined): string | null {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        const last = parsed.pathname.split("/").filter(Boolean).pop();
        if (!last) return null;
        return decodeURIComponent(last) || null;
    } catch {
        return null;
    }
}
function stripExtension(name: string | null): string {
    if (!name) return "";
    const idx = name.lastIndexOf(".");
    return idx > 0 ? name.slice(0, idx) : name;
}
function folderOfUrl(url: string | undefined): string {
    if (!url) return "";
    try {
        const parsed = new URL(url);
        const parts = parsed.pathname.split("/").filter(Boolean);
        return parts.slice(0, -1).join("/");
    } catch {
        return "";
    }
}
function normalizeLanguages(body: any): LanguageOption[] {
    const asPairs = (arr: any[]): LanguageOption[] =>
        arr
            .map((entry: any) => {
                const code = entry?.code ?? entry?.id ?? entry?.value ?? entry?.lang ?? entry?.language ?? null;
                const label = entry?.label ?? entry?.name ?? entry?.display ?? entry?.title ?? entry?.language ?? null;
                if (!code || !label) return null;
                return { code: String(code), label: String(label) };
            })
            .filter(Boolean) as LanguageOption[];

    if (Array.isArray(body)) {
        const out = asPairs(body);
        return out.length ? out : FALLBACK_LANGUAGES;
    }
    if (body && typeof body === "object") {
        const candidates = [
            body.languages,
            body.data?.languages,
            body.result?.languages,
            body.items,
            body.results,
            body.data?.items,
        ].find(Array.isArray);
        if (Array.isArray(candidates)) {
            const out = asPairs(candidates);
            return out.length ? out : FALLBACK_LANGUAGES;
        }
        const entries = Object.entries(body);
        const looksLikeMap = entries.every(([, v]) => typeof v === "string" || typeof v === "number");
        if (looksLikeMap && entries.length) {
            return entries.map(([code, label]) => ({ code, label: String(label) }));
        }
    }
    return FALLBACK_LANGUAGES;
}

function useAutoplayWhenVisible(videoRef: React.RefObject<HTMLVideoElement>) {
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) el.play().catch(() => {});
                else el.pause();
            },
            { threshold: 0.25 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [videoRef]);
}

const VideoCard: React.FC<{
    video: GeneratedFile;
    isSelected: boolean;
    onOpen: (video: GeneratedFile) => void;
    onToggle: (fileId: string) => void;
}> = React.memo(({ video, isSelected, onOpen, onToggle }) => {
    const ref = useRef<HTMLVideoElement>(null);
    useAutoplayWhenVisible(ref);

    return (
        <div className={`relative border rounded-lg overflow-hidden ${isSelected ? "ring-2 ring-blue-600" : ""}`}>
            <button
                type="button"
                className="block w-full aspect-video bg-black"
                onClick={() => onOpen(video)}
                title="Click to preview"
            >
                <video
                    ref={ref}
                    src={video.permalink}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover"
                    onMouseEnter={() => ref.current?.play().catch(() => {})}
                    onMouseLeave={() => ref.current?.pause()}
                />
            </button>
            <div className="px-2 py-1 text-[11px] text-gray-700 flex items-center justify-between">
                <label className="inline-flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={isSelected} onChange={() => onToggle(video.fileId)} />
                    <span>Select</span>
                </label>
            </div>
        </div>
    );
});

const CaptionsIndex: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const { listMyVideoOutputs } = useSubmagicOutputsAPI();
    const { uploadFile: uploadFileApi } = useFileAPI();
    const { getResult: getTaskResult } = useGenerateMediaAPI();

    const [availableVideos, setAvailableVideos] = useState<GeneratedFile[]>([]);
    const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalVideo, setModalVideo] = useState<GeneratedFile | null>(null);

    const [projectTitle, setProjectTitle] = useState<string>("");
    const [languageCode, setLanguageCode] = useState<string>("en");
    const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(FALLBACK_LANGUAGES);
    const languagesLoadedRef = useRef(false);
    const languagesLoadingRef = useRef(false);

    const [alert, setAlert] = useState<AlertProps | null>(null);
    const [pendingState, setPendingState] = useState<PendingState>("idle");
    const [taskQueueId, setTaskQueueId] = useState<string | null>(null);
    const [outputs, setOutputs] = useState<GeneratedFile[]>([]);
    const [selectedOutputIndex, setSelectedOutputIndex] = useState(0);

    const [isDragging, setIsDragging] = useState(false);
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [templates, setTemplates] = useState<TemplateOption[] | null>(null);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState<string | null>(null);

    const pollingIntervalRef = useRef<number | null>(null);
    const pollStartedAtRef = useRef<number>(0);

    useEffect(
        () => () => {
            if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
        },
        []
    );

    const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    const joinApi = (p: string) => `${API_BASE}/${p.replace(/^\/+/, "")}`;

    const mapLibraryItemToGeneratedFile = (o: any): GeneratedFile => ({
        generativeOutputId: o.generative_output_id,
        assetType: o.asset_type,
        fileId: o.file_id,
        createdAt: o.created_at,
        permalink: o.permalink,
        thumbnailImage: o.thumbnail_image ?? null,
        name: o.file_name ?? o.name ?? stripExtension(inferFilenameFromUrl(o.permalink)),
    });

    const refreshLibrary = async () => {
        const data = await listMyVideoOutputs(100, 0);
        const mapped: GeneratedFile[] = data.map(mapLibraryItemToGeneratedFile);
        setAvailableVideos(mapped);
        return mapped;
    };

    useEffect(() => {
        (async () => {
            try {
                await refreshLibrary();
            } catch (e: any) {
                setAlert({ type: "error", message: String(e?.message || e) });
            }
        })();
    }, []);

    const getApiToken = useCallback(
        () =>
            getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUD,
                    scope: "read:current_user",
                },
            }),
        [getAccessTokenSilently]
    );

    const authFetch = useCallback(
        async (url: string, init: RequestInit = {}) => {
            const token = await getApiToken();
            const res = await fetch(url, {
                ...init,
                headers: {
                    Accept: "application/json",
                    ...(init.headers || {}),
                    authorization: `Bearer ${token}`,
                },
                cache: "no-store",
            });
            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`${res.status} — ${res.statusText}${text ? ` — ${text}` : ""}`);
            }
            return res;
        },
        [getApiToken]
    );

    const parseOrFollowForward = useCallback(
        async (res: Response): Promise<any> => {
            const ctype = res.headers.get("content-type") || "";
            if (ctype.includes("application/json")) return res.json();
            const text = await res.text();
            if (text.startsWith("forward:")) {
                const fwd = text.replace(/^forward:/, "").replace(/^\/+/, "");
                const res2 = await authFetch(joinApi(fwd));
                const ctype2 = res2.headers.get("content-type") || "";
                if (ctype2.includes("application/json")) return res2.json();
                const text2 = await res2.text();
                try {
                    return JSON.parse(text2);
                } catch {
                    throw new Error(`Unexpected non-JSON after forward: ${text2.slice(0, 200)}`);
                }
            }
            try {
                return JSON.parse(text);
            } catch {
                throw new Error(`Unexpected response: ${text.slice(0, 200)}`);
            }
        },
        [authFetch]
    );

    const loadLanguagesOnce = useCallback(async () => {
        if (languagesLoadedRef.current || languagesLoadingRef.current) return;
        languagesLoadingRef.current = true;
        try {
            const res = await authFetch(joinApi("submagic/languages"));
            const data = await parseOrFollowForward(res);
            const opts = normalizeLanguages(data);
            setLanguageOptions(opts.length ? opts : FALLBACK_LANGUAGES);
            if (opts.length && !opts.find((l) => l.code === languageCode)) setLanguageCode(opts[0].code);
            languagesLoadedRef.current = true;
        } catch (e: any) {
            setLanguageOptions(FALLBACK_LANGUAGES);
            setAlert({ type: "error", message: `Could not load languages: ${String(e?.message || e)}` });
        } finally {
            languagesLoadingRef.current = false;
        }
    }, [authFetch, parseOrFollowForward, joinApi, languageCode]);

    useEffect(() => {
        if (selectedFileId) loadLanguagesOnce();
    }, [selectedFileId, loadLanguagesOnce]);

    useEffect(() => {
        if (!selectedFileId || projectTitle?.trim()) return;
        const sel = availableVideos.find((v) => v.fileId === selectedFileId);
        if (sel?.name) setProjectTitle(`Captions – ${stripExtension(sel.name)}`);
    }, [selectedFileId, availableVideos, projectTitle]);

    const createProject = async (req: SubmagicCreateProjectRequest) => {
        const res = await authFetch(joinApi("submagic/projects"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(req),
        });
        return (await parseOrFollowForward(res)) as SubmagicTaskQueueResponse;
    };

    const dedupeByFile = (arr: GeneratedFile[]) => {
        const seen = new Set<string>();
        return arr.filter((f) => {
            const key = `${f.fileId}|${f.permalink}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };

    const extractOutputsFromTask = (taskResult: any): GeneratedFile[] => {
        const buckets: any[][] = [];

        if (Array.isArray(taskResult?.outputs)) buckets.push(taskResult.outputs);
        if (Array.isArray(taskResult?.result?.outputs)) buckets.push(taskResult.result.outputs);
        if (Array.isArray(taskResult?.task_result?.outputs)) buckets.push(taskResult.task_result.outputs);
        if (Array.isArray(taskResult?.task_result?.files)) buckets.push(taskResult.task_result.files);
        if (Array.isArray(taskResult?.files)) buckets.push(taskResult.files);
        if (Array.isArray(taskResult?.children)) {
            const childArrays = taskResult.children
                .flatMap((c: any) => [c?.outputs, c?.result?.outputs, c?.task_result?.outputs, c?.task_result?.files])
                .filter(Array.isArray);
            buckets.push(...childArrays);
        }

        const urlArrays = [taskResult?.urls, taskResult?.result?.urls, taskResult?.task_result?.urls].filter(
            Array.isArray
        ) as string[][];
        for (const urls of urlArrays) {
            const mapped = urls
                .filter((u) => typeof u === "string" && u.startsWith("http"))
                .map((u) => {
                    const baseName = stripExtension(inferFilenameFromUrl(u));
                    return {
                        generativeOutputId: crypto.randomUUID(),
                        assetType: "video",
                        fileId: crypto.randomUUID(),
                        createdAt: new Date().toISOString(),
                        permalink: u,
                        thumbnailImage: null,
                        name: baseName || "Captioned",
                    } as GeneratedFile;
                });
            buckets.push(mapped as any[]);
        }

        const flat = buckets.flat().filter(Boolean);
        const normalized: GeneratedFile[] = flat.map((o: any) => ({
            generativeOutputId: o.generative_output_id ?? o.generativeOutputId ?? o.id ?? crypto.randomUUID(),
            assetType: o.asset_type ?? o.assetType ?? "video",
            fileId: o.file_id ?? o.fileId ?? crypto.randomUUID(),
            createdAt: o.created_at ?? o.createdAt ?? new Date().toISOString(),
            permalink: o.permalink ?? o.url ?? o.downloadUrl ?? "",
            thumbnailImage: o.thumbnail_image ?? o.thumbnailImage ?? null,
            name: o.file_name ?? o.name ?? stripExtension(inferFilenameFromUrl(o.permalink ?? o.url)),
        }));

        return normalized.filter((f) => !!f.permalink);
    };

    const findOutputsInLibrary = async (): Promise<GeneratedFile[]> => {
        const latest = await listMyVideoOutputs(100, 0);
        const mapped: GeneratedFile[] = latest.map(mapLibraryItemToGeneratedFile);

        const selected = availableVideos.find((v) => v.fileId === selectedFileId) || null;
        const selectedFolder = folderOfUrl(selected?.permalink || "");
        const selectedBase = stripExtension(inferFilenameFromUrl(selected?.permalink || ""));

        const cutoff = pollStartedAtRef.current ? pollStartedAtRef.current - 60_000 : Date.now() - 60_000;

        const candidates = mapped.filter((m) => {
            const createdAtMs = new Date(m.createdAt).getTime();
            const sameFolder = selectedFolder && folderOfUrl(m.permalink) === selectedFolder;
            const nameIncludes = (m.name || "").toLowerCase().includes((selectedBase || "").toLowerCase());
            const looksLikeCaption =
                (m.name || "").toLowerCase().includes("caption") || (m.name || "").toLowerCase().includes("submagic");
            const recentEnough = createdAtMs >= cutoff;
            const notSameFile = m.fileId !== selectedFileId;
            return m.assetType === "video" && notSameFile && recentEnough && (sameFolder || nameIncludes || looksLikeCaption);
        });

        return dedupeByFile(candidates);
    };

    const startSubmagic = async () => {
        setAlert(null);
        setOutputs([]);
        setTaskQueueId(null);
        setSelectedOutputIndex(0);

        if (!selectedFileId) return setAlert({ type: "error", message: "Select one video." });
        if (!projectTitle.trim()) return setAlert({ type: "error", message: "Title is required." });
        if (!languageCode.trim()) return setAlert({ type: "error", message: "Language is required." });

        setPendingState("submitting");
        try {
            const current = availableVideos.find((v) => v.fileId === selectedFileId) || null;
            const videoUrl = current?.permalink ?? "";

            const resp = await createProject({
                file_ids: [selectedFileId],
                title: projectTitle,
                language: languageCode,
                video_url: videoUrl,
                videoUrl: videoUrl,
            });

            const id = resp?.id;
            if (!id) throw new Error("Missing task id");
            setTaskQueueId(id);
            setPendingState("polling");

            if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
            pollStartedAtRef.current = Date.now();

            pollingIntervalRef.current = window.setInterval(async () => {
                try {
                    const result = await getTaskResult(id);
                    const taskStatus: string | undefined = result?.task_status ?? result?.taskStatus ?? result?.status;

                    const directFiles = extractOutputsFromTask(result);
                    if (directFiles.length > 0) {
                        setOutputs(dedupeByFile(directFiles));
                        setPendingState("done");
                        if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
                        return;
                    }

                    const libraryFiles = await findOutputsInLibrary();
                    if (libraryFiles.length > 0) {
                        setOutputs(dedupeByFile(libraryFiles));
                        setPendingState("done");
                        if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
                        return;
                    }

                    if (taskStatus === "failed") {
                        const message = result?.task_result?.message || result?.result?.message || "Captioning workflow failed.";
                        setAlert({ type: "error", message });
                        setPendingState("error");
                        if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
                        return;
                    }

                    if (taskStatus === "completed") {
                        const lastChance = await findOutputsInLibrary();
                        if (lastChance.length) {
                            setOutputs(dedupeByFile(lastChance));
                            setPendingState("done");
                        } else {
                            setAlert({
                                type: "error",
                                message: "Completed but no outputs were returned yet. Try Refresh — the file may still be indexing.",
                            });
                            setPendingState("error");
                        }
                        if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
                        return;
                    }

                    if (Date.now() - pollStartedAtRef.current > POLL_TIMEOUT_MS) {
                        const lastChance = await findOutputsInLibrary();
                        if (lastChance.length) {
                            setOutputs(dedupeByFile(lastChance));
                            setPendingState("done");
                        } else {
                            setAlert({ type: "error", message: "Timed out waiting for caption output." });
                            setPendingState("error");
                        }
                        if (pollingIntervalRef.current) window.clearInterval(pollingIntervalRef.current);
                    }
                } catch (e: any) {
                    setAlert({ type: "error", message: String(e?.message || e) });
                }
            }, POLL_INTERVAL_MS);
        } catch (e: any) {
            setAlert({ type: "error", message: String(e?.message || e) });
            setPendingState("error");
        }
    };

    const sortedOutputs = useMemo(
        () => outputs.slice().sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
        [outputs]
    );
    const hasMultipleResults = sortedOutputs.length > 1;
    const selectedResult = hasMultipleResults ? sortedOutputs[selectedOutputIndex] : sortedOutputs[0];

    const pickFile = () => fileInputRef.current?.click();

    const acceptVideo = (file: File) => {
        const name = (file.name || "").toLowerCase();
        const ext = name.slice(name.lastIndexOf(".") + 1);
        const byExt = ext === "mp4" || ext === "mov";
        const byMime = file.type?.startsWith("video/");
        return byExt || byMime;
    };

    const uploadVideo = useCallback(
        async (file: File) => {
            if (!acceptVideo(file)) throw new Error("Choose an .mp4 or .mov video");
            const uploaded = await uploadFileApi(
                {
                    files: [file],
                    is_public: false,
                    object_type: "submagic",
                    usage: "submagic_source",
                    generate_thumbnail: false,
                } as any,
                () => {}
            );
            const up = uploaded?.[0];
            if (!up) throw new Error("Upload failed.");
            const newVideo: GeneratedFile = {
                generativeOutputId: up.id || up.file_id || crypto.randomUUID(),
                assetType: "video",
                fileId: (up.id || up.file_id)!,
                createdAt: new Date().toISOString(),
                permalink: up.permalink || URL.createObjectURL(file),
                thumbnailImage: up.thumbnail || null,
                name: up.name || stripExtension(file.name),
            };
            setAvailableVideos((prev) => [newVideo, ...prev]);
            setSelectedFileId(newVideo.fileId);
            if (!projectTitle.trim()) setProjectTitle(`Captions – ${stripExtension(newVideo.name || "Video")}`);
        },
        [uploadFileApi, projectTitle]
    );

    const onFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files ? Array.from(e.target.files) : [];
        e.target.value = "";
        if (!selected.length) return;
        try {
            await uploadVideo(selected[0]);
        } catch (err: any) {
            setAlert({ type: "error", message: String(err?.message || err) });
        }
    };

    useEffect(() => {
        const zone = dropZoneRef.current;
        if (!zone) return;

        const onDragEnter = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            setIsDragging(true);
        };
        const onDragOver = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
        };
        const onDragLeave = (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            setIsDragging(false);
        };
        const onDrop = async (ev: DragEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            setIsDragging(false);
            const files = (ev as any).dataTransfer?.files as FileList | undefined;
            const file = files?.[0];
            if (!file) return;
            try {
                await uploadVideo(file);
            } catch (err: any) {
                setAlert({ type: "error", message: String(err?.message || err) });
            }
        };

        zone.addEventListener("dragenter", onDragEnter as any);
        zone.addEventListener("dragover", onDragOver as any);
        zone.addEventListener("dragleave", onDragLeave as any);
        zone.addEventListener("drop", onDrop as any);
        return () => {
            zone.removeEventListener("dragenter", onDragEnter as any);
            zone.removeEventListener("dragover", onDragOver as any);
            zone.removeEventListener("dragleave", onDragLeave as any);
            zone.removeEventListener("drop", onDrop as any);
        };
    }, [uploadVideo]);

    const [editOpen, setEditOpen] = useState(false);

    const loadTemplates = useCallback(async () => {
        setTemplatesError(null);
        setTemplatesLoading(true);
        try {
            const res = await authFetch(joinApi("submagic/templates"));
            const raw = await parseOrFollowForward(res);
            let items: TemplateOption[] = [];
            const toItem = (t: any): TemplateOption => ({
                id: String(t?.id ?? t?.template_id ?? t?.code ?? t?.value ?? crypto.randomUUID()),
                name: String(t?.name ?? t?.label ?? t?.title ?? "Template"),
                preview: t?.preview ?? t?.thumbnail ?? t?.image ?? null,
            });
            if (Array.isArray(raw)) items = raw.map(toItem).filter((i) => i.id && i.name);
            else if (raw && typeof raw === "object") {
                const arr =
                    raw.templates ||
                    raw.data?.templates ||
                    raw.result?.templates ||
                    raw.items ||
                    raw.results ||
                    raw.data?.items ||
                    [];
                if (Array.isArray(arr)) items = arr.map(toItem).filter((i) => i.id && i.name);
            }
            setTemplates(items);
        } catch (e: any) {
            setTemplatesError(String(e?.message || e));
        } finally {
            setTemplatesLoading(false);
        }
    }, [authFetch, parseOrFollowForward, joinApi]);

    const showPreflightInputs = !!selectedFileId && outputs.length === 0;
    const showPostOutputTools = outputs.length > 0;

    return (
        <AppContainer>
            <div className="relative min-h-[calc(100vh-64px)] bg-[#f7f8fb]">
                {alert && (
                    <div className="container mx-auto px-6 pt-5">
                        <Alert {...alert} onClose={() => setAlert(null)} />
                    </div>
                )}

                <div className="container mx-auto px-6 py-5">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Add captions</h1>
                            <p className="text-sm text-gray-600">Upload or select a video to begin.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".mp4,.mov,video/mp4,video/quicktime"
                                className="hidden"
                                onChange={onFileInputChange}
                            />
                            <button
                                onClick={pickFile}
                                className="px-3 py-1.5 border rounded text-sm bg-white hover:bg-gray-50"
                                title="Upload video"
                            >
                                Upload video
                            </button>
                            <button
                                onClick={async () => {
                                    try {
                                        await refreshLibrary();
                                    } catch (e: any) {
                                        setAlert({ type: "error", message: String(e?.message || e) });
                                    }
                                }}
                                className="px-3 py-1.5 border rounded text-sm bg-white hover:bg-gray-50"
                                title="Refresh"
                            >
                                Refresh
                            </button>
                            {taskQueueId && <div className="text-[12px] text-gray-600">Task {taskQueueId.slice(0, 8)}…</div>}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6 items-start">
                        <div
                            ref={dropZoneRef}
                            className={`rounded-xl bg-white border shadow-sm min-h=[640px] min-h-[640px] flex flex-col relative ${
                                isDragging ? "ring-2 ring-blue-600" : ""
                            }`}
                            title="Drop MP4/MOV here to upload"
                        >
                            <div className="px-4 pt-4 space-y-3">
                                {showPreflightInputs && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <input
                                            value={projectTitle}
                                            onChange={(e) => setProjectTitle(e.target.value)}
                                            placeholder="Project title (required)"
                                            className="md:col-span-2 h-10 px-3 border rounded-lg text-sm"
                                        />
                                        <select
                                            value={languageCode}
                                            onChange={(e) => setLanguageCode(e.target.value)}
                                            className="h-10 px-3 border rounded-lg text-sm bg-white"
                                            title="Language"
                                        >
                                            {languageOptions.map((opt) => (
                                                <option key={opt.code} value={opt.code}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="flex-1 flex items-center justify-center p-6">
                                {!selectedResult ? (
                                    <div className="text-center">
                                        {pendingState === "polling" || pendingState === "submitting" ? (
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="inline-block h-6 w-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                                <div className="text-lg font-medium text-gray-800">Processing…</div>
                                            </div>
                                        ) : selectedFileId ? (
                                            <div className="text-gray-700">
                                                Set the title and language, then click <b>Add captions</b>.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="text-lg font-medium text-gray-800 mb-2">Select one video, click Upload, or drag it here.</div>
                                                <div className="text-gray-600">MP4 or MOV. We’ll upload and select it.</div>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative w-full">
                                        <video
                                            src={selectedResult.permalink}
                                            controls
                                            playsInline
                                            className="bg-black rounded-xl shadow max-w-full h-auto max-h-[540px] mx-auto"
                                        />
                                        {sortedOutputs.length > 1 && (
                                            <div className="mt-3 flex items-center justify-center gap-2">
                                                <button
                                                    onClick={() =>
                                                        setSelectedOutputIndex((i) => (i - 1 + sortedOutputs.length) % sortedOutputs.length)
                                                    }
                                                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                                                    aria-label="Previous result"
                                                    title="Previous"
                                                >
                                                    ◀
                                                </button>
                                                <div className="text-xs text-gray-700">
                                                    Result {selectedOutputIndex + 1} of {sortedOutputs.length}
                                                </div>
                                                <button
                                                    onClick={() => setSelectedOutputIndex((i) => (i + 1) % sortedOutputs.length)}
                                                    className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                                                    aria-label="Next result"
                                                    title="Next"
                                                >
                                                    ▶
                                                </button>
                                            </div>
                                        )}

                                        {showPostOutputTools && (
                                            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                                                <button
                                                    onClick={() => {
                                                        setTemplateModalOpen(true);
                                                        if (!templates) loadTemplates();
                                                    }}
                                                    className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
                                                >
                                                    Browse styles…
                                                </button>
                                                <button
                                                    onClick={() => setEditOpen((v) => !v)}
                                                    className="px-3 py-1.5 rounded border bg-white hover:bg-gray-50 text-sm"
                                                >
                                                    {editOpen ? "Hide captions editor" : "Edit captions"}
                                                </button>
                                            </div>
                                        )}

                                        {showPostOutputTools && editOpen && (
                                            <div className="mt-4 border rounded-xl p-4 max-w-3xl mx-auto">
                                                <div className="text-sm font-semibold mb-2">Captions (read-only preview)</div>
                                                <div className="text-xs text-gray-600">
                                                    Editing pipeline integration is planned. For now, preview the output here.
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="border-t px-4 py-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm text-gray-700">{selectedFileId ? "1 selected" : "0 selected"}</div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={startSubmagic}
                                            disabled={
                                                !selectedFileId ||
                                                !showPreflightInputs ||
                                                pendingState === "submitting" ||
                                                pendingState === "polling"
                                            }
                                            className="h-10 px-4 border rounded-lg flex items-center justify-center text-sm bg-black text-white hover:bg-gray-800 disabled:opacity-50"
                                            title="Add captions"
                                        >
                                            Add captions
                                        </button>
                                        <button
                                            onClick={() => {
                                                setSelectedFileId(null);
                                                setOutputs([]);
                                                setTaskQueueId(null);
                                                setPendingState("idle");
                                                setAlert(null);
                                            }}
                                            className="h-10 px-3 border rounded-lg text-sm bg-white hover:bg-gray-50"
                                            title="Reset selection"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <aside className="rounded-xl bg-white border shadow-sm max-h-[calc(100vh-120px)] overflow-y-auto p-4">
                            <div className="text-sm font-semibold text-gray-900 mb-2">Select a video</div>
                            <p className="text-xs text-gray-500 mb-4">
                                Click a preview to open the player. Use the checkbox to select exactly one item to caption.
                            </p>

                            <div className="grid grid-cols-2 gap-3">
                                {availableVideos.map((video) => {
                                    const isSelected = selectedFileId === video.fileId;
                                    return (
                                        <VideoCard
                                            key={video.fileId}
                                            video={video}
                                            isSelected={isSelected}
                                            onOpen={(vid) => {
                                                setModalVideo(vid);
                                                setModalOpen(true);
                                            }}
                                            onToggle={(fileId) => setSelectedFileId(isSelected ? null : fileId)}
                                        />
                                    );
                                })}
                            </div>
                        </aside>
                    </div>
                </div>

                {modalOpen && modalVideo && (
                    <div
                        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                        onClick={() => setModalOpen(false)}
                    >
                        <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full p-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-medium">{modalVideo.name || "Preview"}</div>
                                <button className="text-sm px-2 py-1 border rounded hover:bg-gray-50" onClick={() => setModalOpen(false)}>
                                    Close
                                </button>
                            </div>
                            <video src={modalVideo.permalink} controls autoPlay playsInline className="w-full max-h-[70vh] bg-black rounded-lg" />
                        </div>
                    </div>
                )}

                {templateModalOpen && (
                    <div
                        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                        onClick={() => setTemplateModalOpen(false)}
                    >
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl p-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-sm font-semibold">Browse styles</div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={loadTemplates}
                                        className="px-2 py-1 text-xs border rounded hover:bg-gray-50"
                                        title="Refresh styles"
                                    >
                                        Refresh
                                    </button>
                                    <button
                                        className="text-sm px-2 py-1 border rounded hover:bg-gray-50"
                                        onClick={() => setTemplateModalOpen(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>

                            {templatesLoading && <div className="py-10 text-center text-sm text-gray-700">Loading styles…</div>}
                            {templatesError && <div className="py-10 text-center text-sm text-red-600">{templatesError}</div>}

                            {!!templates && !templatesLoading && (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                        {templates.map((t) => (
                                            <div key={t.id} className="border rounded-lg overflow-hidden">
                                                <div className="aspect-video bg-gray-100 flex items-center justify-center">
                                                    {t.preview ? (
                                                        <img src={t.preview} alt={t.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="text-[11px] text-gray-500">No preview</div>
                                                    )}
                                                </div>
                                                <div className="px-2 py-2 text-[12px]">{t.name}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 text-xs text-gray-600">
                                        Styling is shown for reference after the first render. Applying styles to an existing project can
                                        be added later.
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AppContainer>
    );
};

export default CaptionsIndex;
